import type { ApiClient } from '@carbonwebmail/api-client'
import type { SegmentDto } from '@carbonwebmail/contracts'
import type { SegmentSort } from '@carbonwebmail/domain'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { AddColumn } from '../../ui/AddColumn.js'
import { Alert } from '../../ui/Alert.js'
import { Async } from '../../ui/Async.js'
import { Checkbox } from '../../ui/Checkbox.js'
import { ColumnDialog } from '../../ui/ColumnDialog.js'
import { ColumnMenu } from '../../ui/ColumnMenu.js'
import { DeleteColumnDialog } from '../../ui/DeleteColumnDialog.js'
import { count } from '../../ui/format.js'
import { EmptyNote } from '../../ui/PagedScreen.js'
import { TableSkeleton } from '../../ui/Pager.js'
import type { HeadCell } from '../../ui/Table.js'
import { Table, Td, Tr, TrEmpty } from '../../ui/Table.js'
import { fieldFor } from '../../ui/useDeclaredColumns.js'
import { everyRowTicked } from '../../ui/useSelection.js'
import { useMay } from '../../ui/yourRole.js'
import { INTO_SEGMENT } from '../import/useImport.js'
import { NewSegmentDialog } from './NewSegmentDialog.js'
import { RenameSegmentDialog } from './RenameSegmentDialog.js'
import { SaveSegmentViewDialog } from './SaveSegmentViewDialog.js'
import { SegmentBulkEdit } from './SegmentBulkEdit.js'
import { SegmentMenu } from './SegmentMenu.js'
import { SegmentsToolbar } from './SegmentsToolbar.js'
import { SegmentViewTabs } from './SegmentViewTabs.js'
import type { BoardSegmentColumn } from './segmentColumns.js'
import { valueIn } from './segmentColumns.js'
import { useSegments } from './useSegments.js'
import { useSegmentViews } from './useSegmentViews.js'

/**
 * The segments board.
 *
 * **A segment list is a table of rows like any other**, which is the owner's
 * instruction and the reason this screen shares its parts rather than having
 * its own: the same ticking as the contacts board (`useSelection`), the same
 * `⋯` on a row, the same reporting when something is done to a selection. A
 * simplified list of its own is what this was, and the difference showed the
 * moment somebody wanted to rename one.
 *
 * Each row says what the segment actually asks, in words. "3 rules" tells you
 * nothing about whether this is the list you meant to send to; "subscribed, and
 * not hard bounced" tells you at a glance — and the moment that matters is
 * picking an audience for a campaign, when opening every one to check is the
 * thing nobody does.
 *
 * **Searching and ordering happen in the browser**, unlike every other list
 * here. A tenant has tens of segments and the whole list already arrives in one
 * response; paging it would be a second answer to which segments exist. See
 * `useSegments` for what would have to change if that stops being true.
 */
export function SegmentsPage({ client }: { readonly client: ApiClient }) {
  /*
   * A viewer reads the segments and creates none.
   *
   * Importing contacts is a contacts write rather than a segments one, and is
   * asked for separately — the two are the same answer for every role in the
   * table today, and writing them as one would be the place that stopped being
   * true without anybody noticing.
   */
  const mayWrite = useMay('segments', 'write')
  const mayImport = useMay('contacts', 'write')

  /**
   * One instance of the views, owned by the screen that draws the tabs.
   *
   * The contacts board learnt this the hard way: two instances meant saving
   * reloaded one list while the strip was drawn from the other, and a view was
   * created that nothing showed.
   */
  const views = useSegmentViews({ client })
  const segments = useSegments({ client, views: views.views })
  const navigate = useNavigate()
  const { state, shown, picked, search, sort, notice, problem, busy } = segments

  /** The segment being edited, held whole: saving replaces it with its rules. */
  const [renaming, setRenaming] = useState<SegmentDto | undefined>(undefined)
  /**
   * Deleting asks first, in the bar rather than in a dialog.
   *
   * There is no undo, and what a segment is worth is the thinking in its rules
   * rather than the row itself.
   */
  const [confirming, setConfirming] = useState(false)
  /** The declared column being renamed or retyped, and the one being deleted. */
  const [editingColumn, setEditingColumn] = useState<BoardSegmentColumn | undefined>(undefined)
  const [removingColumn, setRemovingColumn] = useState<BoardSegmentColumn | undefined>(undefined)
  /** Naming the arrangement on screen. Local: it is done here and then left. */
  const [savingView, setSavingView] = useState(false)
  /**
   * Making a segment, over the board rather than at a route of its own.
   *
   * Local state rather than the url for the reason the contacts board's dialogs
   * are: this is done to the board and then left, and the thing somebody came
   * to see is the new row appearing in the table behind it.
   */
  const [creating, setCreating] = useState(false)

  return (
    <>
      <header className="mb-5 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-bold text-[22px] text-t1 -tracking-[0.01em]">Segments</h1>
          <p className="mt-1 text-[13px] text-t2">
            Saved questions about your contacts. Answered when a campaign is sent, never when the
            segment is saved.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/*
            The same link the contacts board carries, because it is the same
            import: it adds contacts, and the segment they are connected to is
            chosen on that screen. A segment's own `⋯` arrives there with itself
            already picked.
          */}
          {!mayImport ? null : (
            <Link
              className="rounded-pill border border-line px-4 py-2.5 font-semibold text-[13px] text-t2 hover:text-t1"
              to="/contacts/import"
            >
              Import from CSV
            </Link>
          )}
          {!mayWrite ? null : (
            <button
              className="rounded-pill bg-brand px-5 py-2.5 font-bold text-[14px] text-brand-contrast"
              onClick={() => setCreating(true)}
              type="button"
            >
              New segment
            </button>
          )}
        </div>
      </header>

      <SegmentViewTabs
        chosen={segments.viewId}
        onChoose={segments.chooseView}
        onRemove={(viewId) => void views.remove(viewId)}
        onSave={() => setSavingView(true)}
        views={views.views}
      />

      {savingView ? (
        <SaveSegmentViewDialog
          hidden={[...segments.hidden]}
          onClose={() => setSavingView(false)}
          onSave={views.save}
          rules={segments.asked}
          search={search}
          sort={sort}
        />
      ) : null}

      <SegmentsToolbar
        columns={segments.allColumns}
        hidden={segments.hidden}
        onExport={segments.exportShown}
        onHidden={segments.setHidden}
        onNewRule={segments.keyed}
        onRules={segments.setRules}
        onSaveAsView={() => setSavingView(true)}
        onSearch={segments.setSearch}
        onSort={segments.setSort}
        rules={segments.rules}
        search={search}
        shown={shown.length}
        sort={sort}
      />

      {problem === undefined ? null : <Alert>{problem}</Alert>}
      {notice === undefined ? null : <Alert tone="ok">{notice}</Alert>}

      {picked.length === 0 ? null : (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-card border border-line bg-surface-2 px-4 py-3">
          <p className="font-semibold text-[13px] text-t1">
            <span className="font-num tabular-nums">{count(picked.length)}</span> selected
          </p>

          {/*
            Only the declared columns can be set this way, and the control is
            absent until one exists. `SegmentBulkEdit` says why a name and a
            rule list are not things to share across a selection.
          */}
          <SegmentBulkEdit
            busy={busy}
            fields={segments.fields}
            onApply={(key, value, label) => void segments.editMany(picked, key, value, label)}
            picked={picked.length}
          />

          {/*
            Said before it happens, because it is the one thing here that cannot
            be undone — and a segment a campaign was sent to is kept whatever
            this says, because that audience is part of the record.
          */}
          {confirming ? (
            <span className="flex items-center gap-2">
              <button
                className="rounded-pill bg-danger px-4 py-2 font-semibold text-[13px] text-brand-contrast disabled:opacity-60"
                disabled={busy}
                onClick={() => {
                  setConfirming(false)
                  void segments.removeMany(picked)
                }}
                type="button"
              >
                Delete {count(picked.length)}
              </button>
              <button
                className="font-medium text-[12px] text-t3 hover:text-t1"
                onClick={() => setConfirming(false)}
                type="button"
              >
                Keep them
              </button>
            </span>
          ) : (
            <button
              className="rounded-pill border border-line px-4 py-2 font-semibold text-[13px] text-t2 hover:text-danger"
              disabled={busy}
              onClick={() => setConfirming(true)}
              type="button"
            >
              Delete {count(picked.length)}
            </button>
          )}
        </div>
      )}

      {editingColumn === undefined ? null : (
        <ColumnDialog
          column={editingColumn}
          field={fieldFor(segments.fields, editingColumn.key)}
          onClose={() => setEditingColumn(undefined)}
          onSave={(change) =>
            // Two places a name is stored, and the column says which: one the
            // board came with is an override keyed on its id, a declared one is
            // its own row and carries its kind and choices with the name.
            editingColumn.kind === 'base'
              ? segments.renameColumn(editingColumn.id, change.label)
              : segments.editColumn(editingColumn.key ?? '', change)
          }
        />
      )}

      {removingColumn === undefined ? null : (
        <DeleteColumnDialog
          column={removingColumn}
          onClose={() => setRemovingColumn(undefined)}
          onDelete={() => segments.removeColumn(removingColumn.key ?? '')}
        />
      )}

      {creating ? (
        <NewSegmentDialog
          client={client}
          fields={segments.fields}
          onClose={() => setCreating(false)}
          onCreated={() => void segments.retry()}
        />
      ) : null}

      {renaming === undefined ? null : (
        <RenameSegmentDialog
          busy={busy}
          onClose={() => setRenaming(undefined)}
          fields={segments.fields}
          onSave={(name, custom) => void segments.edit(renaming, name, custom)}
          segment={renaming}
        />
      )}

      <Async state={state} skeleton={<TableSkeleton />} onRetry={() => void segments.retry()}>
        {(result) => (
          /*
            The table is drawn whether or not it has rows, so its headings —
            and the `⋯` on each one that renames, retypes and deletes a column
            — are reachable on a board nobody has added to yet. It used to be
            swapped for a card, which took the columns with it.
          */
          <Table
            head={[
              tick(shown, picked, segments.toggleAllOnPage),
              ...segments.columns.map((column) =>
                heading(column, segments, setEditingColumn, setRemovingColumn),
              ),
              {
                key: 'add',
                node: (
                  <AddColumn client={client} onAdded={segments.reloadColumns} subject="segments" />
                ),
              },
              '',
            ]}
            minWidth={760}
          >
            {shown.length === 0 ? (
              <TrEmpty span={segments.columns.length + 3}>
                {result.segments.length === 0 ? (
                  <EmptyNote heading="No segments yet">
                    A segment is a saved question — everyone at one company, everyone who has not
                    been mailed in ninety days. Campaigns send to one, or to your whole list.
                  </EmptyNote>
                ) : (
                  <EmptyNote heading="No segment matches that">
                    Nothing here is called "{search}". Clearing the box brings the rest back.
                  </EmptyNote>
                )}
              </TrEmpty>
            ) : (
              shown.map((segment) => (
                <Row
                  columns={segments.columns}
                  key={segment.segmentId}
                  onDelete={() => void segments.removeMany([segment.segmentId])}
                  onExport={() => void segments.exportMembers(segment)}
                  onImport={() =>
                    navigate(
                      `/contacts/import?${INTO_SEGMENT}=${encodeURIComponent(segment.segmentId)}`,
                    )
                  }
                  onEdit={() => setRenaming(segment)}
                  onTick={() => segments.toggle(segment.segmentId)}
                  segment={segment}
                  ticked={picked.includes(segment.segmentId)}
                />
              ))
            )}
          </Table>
        )}
      </Async>
    </>
  )
}

/**
 * A column heading, with the menu of what can be done to that column.
 *
 * **Every column has one now**, which is the contacts board's shape and was not
 * this one's: the four a segment always has offered nothing at all, so ordering
 * by a column and hiding it could only be reached from the toolbar while the
 * contacts board offered both from the heading.
 *
 * What each column allows still differs, and the absent handlers are what say
 * so. A declared column is the organisation's, so renaming, retyping and
 * deleting it are theirs; one the board came with can be renamed — the
 * override lands in `segment_column_labels` (migration 0035) — but not retyped
 * or deleted, because the data would still be there.
 */
function heading(
  column: BoardSegmentColumn,
  segments: ReturnType<typeof useSegments>,
  onEdit: (column: BoardSegmentColumn) => void,
  onDelete: (column: BoardSegmentColumn) => void,
): HeadCell {
  const declared = column.kind === 'field'

  return {
    key: column.id,
    node: (
      <span className="group/head flex items-center justify-between gap-1">
        {column.label}
        <ColumnMenu
          kind={fieldFor(segments.fields, column.key)?.type}
          label={column.label}
          onDelete={declared ? () => onDelete(column) : undefined}
          onHide={
            column.fixed
              ? undefined
              : () => segments.setHidden(new Set([...segments.hidden, column.id]))
          }
          onRename={() => onEdit(column)}
          onRetype={declared ? () => onEdit(column) : undefined}
          onSort={
            column.sortable
              ? (direction) =>
                  segments.setSort(
                    direction === undefined
                      ? undefined
                      : { column: column.id as SegmentSort['column'], direction },
                  )
              : undefined
          }
          sorted={segments.sort?.column === column.id ? segments.sort.direction : undefined}
        />
      </span>
    ),
  }
}

/**
 * The header box: everybody on screen, or nobody.
 *
 * A heading that is a control rather than a word, so it carries its own key —
 * a header row cannot be keyed by position when one of its cells has no text.
 */
function tick(
  shown: readonly SegmentDto[],
  picked: readonly string[],
  onToggleAll: (ids: readonly string[]) => void,
): HeadCell {
  const ids = shown.map((segment) => segment.segmentId)

  return {
    key: 'tick',
    node: (
      <Checkbox
        checked={everyRowTicked(ids, picked)}
        indeterminate={picked.length > 0 && !everyRowTicked(ids, picked)}
        label="Select every segment shown"
        onChange={() => onToggleAll(ids)}
      />
    ),
  }
}

function Row({
  segment,
  columns,
  ticked,
  onTick,
  onEdit,
  onExport,
  onImport,
  onDelete,
}: {
  readonly segment: SegmentDto
  /** Only the ones still showing, so hiding one removes its cell as well as its heading. */
  readonly columns: readonly BoardSegmentColumn[]
  readonly ticked: boolean
  readonly onTick: () => void
  readonly onEdit: () => void
  readonly onExport: () => void
  readonly onImport: () => void
  readonly onDelete: () => void
}) {
  return (
    // `Tr` carries the hover group, so the row’s own `⋯` appears on hover the same
    // way a column heading’s does. It is still reachable from a keyboard: the
    // trigger shows itself on focus rather than only under a pointer.
    <Tr>
      <Td>
        <Checkbox checked={ticked} label={`Select ${segment.name}`} onChange={onTick} />
      </Td>
      {columns.map((column) =>
        // The name is the way back to the segment, so it is the one cell that
        // is a link rather than text.
        column.id === 'name' ? (
          <Td key={column.id} strong>
            <Link className="text-brand" to={`/segments/${segment.segmentId}`}>
              {segment.name}
            </Link>
          </Td>
        ) : (
          <Td key={column.id}>{valueIn(segment, column)}</Td>
        ),
      )}
      <Td>
        <span className="flex items-center justify-end gap-3">
          <Link
            className="font-medium text-[13px] text-brand"
            to={`/segments/${segment.segmentId}/members`}
          >
            Who it reaches
          </Link>
          <SegmentMenu
            onDelete={onDelete}
            onExport={onExport}
            onEdit={onEdit}
            onImport={onImport}
            segment={segment}
          />
        </span>
      </Td>
    </Tr>
  )
}
